import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const parallax = useTransform(scrollY, [0, 700], [0, 80]);

  return (
    <section data-testid="hero-section" className="relative px-6 pb-24 pt-36 lg:pb-40 lg:pt-48">
      <div className="mx-auto grid max-w-6xl items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <h1 className="font-serif text-4xl leading-[1.08] text-[#3F5A47] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Udvikling begynder</MaskedLine>
            <MaskedLine delay={0.32}>med forståelse.</MaskedLine>
          </h1>
          <motion.div
            className="mt-9 h-px w-16 bg-[#3F5A47]"
            initial={reduce ? false : { scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
            style={{ transformOrigin: "left" }}
          />
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.65 }}
          >
            <p className="mt-10 max-w-xl text-base leading-loose text-[#2F3338]/85">
              En traumebevidst tilgang handler ikke om at definere mennesker ud fra deres traumer.
              Den handler om at forstå de erfaringer og livsbetingelser, der har formet vores måder
              at være i verden på, og om at skabe betingelser, der understøtter trivsel og
              udvikling.
            </p>
            <p className="mt-5 max-w-xl text-base leading-loose text-[#2F3338]/85">
              I denne forståelse findes også ressourcer, mening, modstandskraft og muligheder for
              udvikling. Og vi går fra viden og forståelse til en traumebevidst tilgang, når vi
              omsætter det til praksis i vores møder, organisering, kultur og de rammer, vi skaber.
            </p>
          </motion.div>
          <motion.div
            className="mt-12 flex flex-wrap gap-4"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.85 }}
          >
            <Link
              to="/vores-tilgang"
              data-testid="hero-cta-vores-tilgang"
              className="rounded-full bg-[#3F5A47] px-7 py-3 text-sm font-medium text-[#FAF7F1] transition-colors duration-300 hover:bg-[#2F3338]"
            >
              Vores tilgang
            </Link>
            <Link
              to="/vi-tilbyder"
              data-testid="hero-cta-se-tilbud"
              className="rounded-full border border-[#3F5A47]/40 px-7 py-3 text-sm font-medium text-[#3F5A47] transition-colors duration-300 hover:border-[#3F5A47] hover:bg-[#3F5A47]/5"
            >
              Se hvad vi tilbyder
            </Link>
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <motion.div
            style={reduce ? undefined : { y: parallax }}
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.4 }}
          >
            <img
              src="/brand/hero-dandelion.png"
              alt="Håndtegnet mælkebøtte med drivende frø"
              className="mx-auto w-full max-w-[260px] sm:max-w-xs lg:max-w-sm"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
